/**
 * Client-side consent helpers.
 *
 * Single source of truth for reading/writing the user's cookie/GDPR
 * consent choice. Any non-essential code (analytics, marketing tags,
 * heatmaps, etc.) MUST call `getConsent()` before loading.
 *
 * Storage key: `purebox.consent.v1`
 */

export interface ConsentChoices {
  essential: true
  analytics: boolean
  marketing: boolean
  preferences: boolean
}

export interface ConsentRecord {
  version: string
  choices: ConsentChoices
  gpcSignal: boolean
  timestamp: string
}

export const COOKIE_POLICY_VERSION = 'v1';
export const CONSENT_STORAGE_KEY = 'purebox.consent.v1';
export const CONSENT_VERSION = COOKIE_POLICY_VERSION;

/**
 * Countries where the EEA/UK/CH cookie banner MUST be shown.
 * Source: EU 27 + EEA (IS, LI, NO) + UK + CH.
 */
export const REGULATED_COUNTRIES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR',
  'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL',
  'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'IS', 'LI', 'NO',
  'GB', 'CH',
]);

export const REGULATED_TIMEZONE_PREFIXES = [
  'Europe/',
  'Atlantic/Reykjavik',
  'Atlantic/Faroe',
];

export const DEFAULT_REJECT: ConsentChoices = {
  essential: true,
  analytics: false,
  marketing: false,
  preferences: false,
};

export const DEFAULT_ACCEPT_ALL: ConsentChoices = {
  essential: true,
  analytics: true,
  marketing: true,
  preferences: true,
};

/**
 * Reads the stored consent. Returns null if the user hasn't chosen yet.
 */
export function getConsent(): ConsentRecord | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentRecord;
    if (!parsed?.choices || parsed?.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Writes the consent choice to localStorage.
 */
export function saveConsent(record: ConsentRecord): boolean {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}

/**
 * Returns true when the user has granted consent for the given bucket.
 * Callers SHOULD use this before loading any non-essential script.
 */
export function hasConsentFor(bucket: keyof ConsentChoices): boolean {
  if (bucket === 'essential') return true;
  const rec = getConsent();
  return Boolean(rec?.choices?.[bucket]);
}

/**
 * Returns a stable anonymous session ID (sessionStorage-scoped).
 * Used to correlate consent logs with the visitor even when they
 * haven't authenticated yet.
 */
export function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return 'server';
  const key = 'purebox.session.id';
  try {
    const existing = window.sessionStorage.getItem(key);
    if (existing) return existing;
    const id = `sess_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    window.sessionStorage.setItem(key, id);
    return id;
  } catch {
    return `sess_${Date.now().toString(36)}`;
  }
}

/**
 * Returns the Global Privacy Control signal from the browser (if present).
 */
export function detectGpc(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return Boolean(nav.globalPrivacyControl);
}

/**
 * Heuristic fallback when the server didn't return a country: treat
 * browsers with a Europe/ timezone as "regulated" so the banner shows.
 */
export function heuristicIsRegulated(): boolean {
  if (typeof Intl === 'undefined') return false;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return REGULATED_TIMEZONE_PREFIXES.some((p) => tz.startsWith(p));
  } catch {
    return false;
  }
}

/**
 * Decides whether to show the consent banner.
 *
 * We require an explicit analytics choice for every non-GPC visitor,
 * regardless of region, so analytics tools can be intentionally enabled.
 */
export function shouldShowBanner(opts: {
  country: string | null
  gpcSignal: boolean
  hasExistingConsent: boolean
}): boolean {
  if (opts.hasExistingConsent) return false;
  if (opts.gpcSignal) return false;
  return true;
}

export function emitConsentChanged(record: ConsentRecord): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('purebox:consent-changed', { detail: record }));
}
